import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import {
  getManualTaxLots,
  deleteManualTaxLot,
  ManualTaxLot,
} from '../../api/tax';
import { useTaxYearStore } from '../../stores/useTaxYearStore';
import { useAutoRefreshTrigger } from '../../stores/useGlobalAutoRefreshStore';
import { formatUsd, formatCrypto, formatDate, formatOrderId } from './taxReportingUtils';
import DataTable, { DataTableFilter } from '../shared/DataTable';
import { usePortfolioRegistry } from '../../hooks/usePortfolioRegistry';
import ManualTaxLotModal from './ManualTaxLotModal';
import { InboxProvenanceBadge } from './InboxProvenanceBadge';

// Server-paginated. See CoinbaseTransactionsView.tsx for the canonical idiom.

type ModalState =
  | { kind: 'closed' }
  | { kind: 'create' }
  | { kind: 'edit'; lot: ManualTaxLot }
  | { kind: 'delete'; lot: ManualTaxLot };

export const ManualTaxLotsView: React.FC = () => {
  const taxYear = useTaxYearStore((s) => s.selectedYear);
  const refreshTrigger = useAutoRefreshTrigger('tax-reporting');
  const queryClient = useQueryClient();

  const [portfolioId, setPortfolioId] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [sorting, setSorting] = useState<SortingState>([{ id: 'disposal_date', desc: true }]);
  const sortBy = sorting[0]?.id;
  const sortDir: 'asc' | 'desc' | undefined = sorting[0] ? (sorting[0].desc ? 'desc' : 'asc') : undefined;

  const [modal, setModal] = useState<ModalState>({ kind: 'closed' });
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const params: Record<string, string | number | undefined> = {
    taxYear,
    portfolioId: portfolioId || undefined,
    sortBy,
    sortDir,
    page: pageIndex + 1,
    pageSize,
  };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: [
      'tax',
      'manual-tax-lots',
      taxYear,
      portfolioId,
      sortBy,
      sortDir,
      pageIndex,
      pageSize,
      refreshTrigger,
    ],
    queryFn: () => getManualTaxLots(params),
  });

  const items = data?.items ?? [];
  const total = data?.metadata.total ?? 0;
  const pageCount = data ? Math.max(1, Math.ceil(total / pageSize)) : -1;

  // Portfolio dropdown options from the canonical registry (not from the
  // current page's items). See src/hooks/usePortfolioRegistry.ts.
  const { options: portfolioOptions } = usePortfolioRegistry();

  const onFilterChange = (setter: (v: string) => void) => (v: string) => {
    setter(v);
    setPageIndex(0);
  };

  const filters: DataTableFilter[] = [
    {
      id: 'portfolio_id',
      label: 'Portfolio',
      value: portfolioId,
      options: portfolioOptions,
      onChange: onFilterChange(setPortfolioId),
    },
  ];

  const deleteMutation = useMutation({
    mutationFn: ({ lotId, reason }: { lotId: number; reason: string }) =>
      deleteManualTaxLot(lotId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tax', 'manual-tax-lots'] });
      setModal({ kind: 'closed' });
      setDeleteReason('');
      setDeleteError(null);
    },
    onError: (err) => {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete manual tax lot');
    },
  });

  const columns = useMemo<ColumnDef<ManualTaxLot>[]>(
    () => [
      { accessorKey: 'lot_id', header: 'Lot ID' },
      { accessorKey: 'portfolio_id', header: 'Portfolio' },
      { accessorKey: 'product_id', header: 'Product' },
      { accessorKey: 'acquisition_source_type', header: 'Acq Src' },
      {
        accessorKey: 'acquisition_order_id',
        header: 'Acq Order',
        cell: (info) => (
          <span className="order-id-cell">{formatOrderId(info.getValue() as string | null)}</span>
        ),
        enableSorting: false,
      },
      {
        accessorKey: 'acquisition_date',
        header: 'Acq Date',
        cell: (info) => (
          <span className="date-cell">{formatDate(info.getValue() as string)}</span>
        ),
      },
      {
        accessorKey: 'acquisition_cost',
        header: 'Acq Cost',
        cell: (info) => formatUsd(info.getValue() as string),
      },
      { accessorKey: 'disposal_source_type', header: 'Disp Src' },
      {
        accessorKey: 'disposal_order_id',
        header: 'Disp Order',
        cell: (info) => (
          <span className="order-id-cell">{formatOrderId(info.getValue() as string | null)}</span>
        ),
        enableSorting: false,
      },
      {
        accessorKey: 'disposal_date',
        header: 'Disp Date',
        cell: (info) => (
          <span className="date-cell">{formatDate(info.getValue() as string)}</span>
        ),
      },
      {
        accessorKey: 'proceeds',
        header: 'Proceeds',
        cell: (info) => formatUsd(info.getValue() as string),
      },
      {
        id: 'gain_loss',
        header: 'Gain / Loss',
        cell: (info) => {
          const lot = info.row.original;
          const gainLoss =
            parseFloat(lot.proceeds ?? '0') -
            parseFloat(lot.acquisition_cost ?? '0') -
            parseFloat(lot.fees_acquisition ?? '0') -
            parseFloat(lot.fees_disposal ?? '0');
          const cls = gainLoss >= 0 ? 'pnl-positive' : 'pnl-negative';
          return <span className={cls}>{formatUsd(gainLoss.toFixed(2))}</span>;
        },
      },
      {
        accessorKey: 'quantity',
        header: 'Quantity',
        cell: (info) => formatCrypto(info.getValue() as string),
      },
      { accessorKey: 'holding_period', header: 'Holding' },
      {
        accessorKey: 'source_reference',
        id: 'source_reference',
        header: 'Source',
        enableSorting: false,
        cell: (info) => (
          <InboxProvenanceBadge
            sourceReference={info.getValue() as string | undefined}
          />
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="row-actions">
              <button
                type="button"
                className="modal-pill-button modal-pill-preview"
                onClick={() => setModal({ kind: 'edit', lot: row })}
              >
                Edit
              </button>
              <button
                type="button"
                className="modal-pill-button modal-pill-delete"
                onClick={() => {
                  setDeleteReason('');
                  setDeleteError(null);
                  setModal({ kind: 'delete', lot: row });
                }}
              >
                Delete
              </button>
            </div>
          );
        },
      },
    ],
    []
  );

  return (
    <div aria-label="Manual Tax Lots view">
      <DataTable<ManualTaxLot>
        data={items}
        columns={columns}
        serverSorting={{
          state: sorting,
          onChange: (s) => {
            setSorting(s);
            setPageIndex(0);
          },
        }}
        filters={filters}
        headerActions={
          <button
            type="button"
            className="modal-pill-button modal-pill-primary"
            onClick={() => setModal({ kind: 'create' })}
          >
            New Manual Tax Lot
          </button>
        }
        loadingMessage="Loading manual tax lots…"
        isLoading={isLoading}
        isError={isError}
        errorMessage={error instanceof Error ? error.message : 'Failed to load manual tax lots'}
        emptyMessage="No manual tax lots for these filters."
        serverPagination={{
          pageIndex,
          pageSize,
          pageCount,
          totalRows: total,
          onPageIndexChange: setPageIndex,
          onPageSizeChange: (size) => {
            setPageSize(size);
            setPageIndex(0);
          },
        }}
      />

      {modal.kind === 'create' && (
        <ManualTaxLotModal
          mode="create"
          onClose={() => setModal({ kind: 'closed' })}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['tax', 'manual-tax-lots'] });
          }}
        />
      )}

      {modal.kind === 'edit' && (
        <ManualTaxLotModal
          mode="edit"
          initial={modal.lot}
          onClose={() => setModal({ kind: 'closed' })}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['tax', 'manual-tax-lots'] });
          }}
        />
      )}

      {modal.kind === 'delete' && (
        <div className="modal-overlay" onClick={() => setModal({ kind: 'closed' })}>
          <div
            className="modal-content delete-confirmation-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>Delete Manual Tax Lot</h2>
              <button
                type="button"
                className="close-button"
                aria-label="Close delete confirmation"
                onClick={() => setModal({ kind: 'closed' })}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p className="modal-description">
                Soft-deleting lot {modal.lot.lot_id}. Provide a reason for the audit log.
              </p>
              <div className="form-group">
                <label htmlFor="mtl-delete-reason">Reason</label>
                <input
                  id="mtl-delete-reason"
                  type="text"
                  className="form-input"
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                />
              </div>
              {deleteError && (
                <div className="delete-warning-box" role="alert">
                  <strong className="delete-warning-title">Error:</strong>
                  <span className="delete-warning-text">{deleteError}</span>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="modal-pill-button modal-pill-cancel"
                onClick={() => setModal({ kind: 'closed' })}
                disabled={deleteMutation.isPending}
              >
                Cancel
              </button>
              <button
                type="button"
                className="modal-pill-button modal-pill-delete"
                disabled={deleteMutation.isPending || deleteReason.trim() === ''}
                onClick={() =>
                  deleteMutation.mutate({
                    lotId: modal.lot.lot_id,
                    reason: deleteReason.trim(),
                  })
                }
              >
                {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManualTaxLotsView;
